// ---------------------------------------------------------------------------
// PlanogramSelection
//
// Typed, immutable wrapper around the "selected cell keys" concept that was
// previously a raw `Set<string>` passed around and mutated ad-hoc across
// PlanogramDetails (selectedCells state + handleCellSelection/
// handleBulkCellSelection/handleSelectAllCells/checkAllSelectedCellsHaveProducts),
// PlanogramVisual (row/column header "select all" checkboxes), and
// PlanogramTabular (isAllSelected/handleSelectAll).
//
// Same immutable-instance pattern as PlanogramEditor: every mutator returns
// a NEW PlanogramSelection. `usePlanogramSelection`/consuming hooks just do
// `setSelection(selection.toggle(key))`.
// ---------------------------------------------------------------------------

import { CellKey, PlanogramCellData, PlanogramGrid, cellKey } from './types';

export class PlanogramSelection {
    private constructor(private readonly keys: ReadonlySet<CellKey>) {}

    static empty(): PlanogramSelection {
        return new PlanogramSelection(new Set());
    }

    static of(keys: Iterable<CellKey>): PlanogramSelection {
        return new PlanogramSelection(new Set(keys));
    }

    get size(): number {
        return this.keys.size;
    }

    get isEmpty(): boolean {
        return this.keys.size === 0;
    }

    has(key: CellKey): boolean {
        return this.keys.has(key);
    }

    toArray(): CellKey[] {
        return Array.from(this.keys);
    }

    toSet(): Set<CellKey> {
        return new Set(this.keys);
    }

    toggle(key: CellKey): PlanogramSelection {
        const next = new Set(this.keys);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return new PlanogramSelection(next);
    }

    /** Adds or removes a batch of keys atomically (used by row/column "select all" headers and drag-select range commits). */
    setMany(keys: Iterable<CellKey>, shouldSelect: boolean): PlanogramSelection {
        const next = new Set(this.keys);
        for (const key of keys) {
            if (shouldSelect) next.add(key);
            else next.delete(key);
        }
        return new PlanogramSelection(next);
    }

    clear(): PlanogramSelection {
        return PlanogramSelection.empty();
    }

    /** True if every key in `keys` is present in this selection (used for row/column header checkbox "all checked" state). */
    containsAll(keys: Iterable<CellKey>): boolean {
        for (const key of keys) {
            if (!this.keys.has(key)) return false;
        }
        return true;
    }

    /**
     * Every key in the rectangle spanned by `anchor` and `focus` (inclusive
     * on both ends), restricted to cells that are actually visible (not
     * merge followers) — same definition `visibleKeysFor` already uses.
     * Pure coordinate math, no grid traversal order assumptions, shared by
     * GH #555's Shift+Arrow keyboard extension (PlanogramVisual's
     * rectangular grid) and the mouse drag-select's `gridRangeResolvers`
     * (which this intentionally mirrors, rather than re-deriving the same
     * min/max-then-iterate logic a second time with different rounding).
     */
    static rectangleKeys(
        grid: PlanogramGrid,
        anchor: { rowIndex: number; colIndex: number },
        focus: { rowIndex: number; colIndex: number },
    ): CellKey[] {
        const minRow = Math.min(anchor.rowIndex, focus.rowIndex);
        const maxRow = Math.max(anchor.rowIndex, focus.rowIndex);
        const minCol = Math.min(anchor.colIndex, focus.colIndex);
        const maxCol = Math.max(anchor.colIndex, focus.colIndex);

        const keys: CellKey[] = [];
        for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
                const cell = grid?.[r]?.[c];
                if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                    keys.push(cellKey(r, c));
                }
            }
        }
        return keys;
    }

    /** Every visible (non-merge-follower) cell key across the whole grid, or one row if rowIndex is given. */
    static visibleKeysFor(grid: PlanogramGrid, rows: number, columns: number, rowIndex = -1): CellKey[] {
        const keys: CellKey[] = [];
        const rowIndexes = rowIndex === -1 ? Array.from({ length: rows }, (_, i) => i) : [rowIndex];

        rowIndexes.forEach(r => {
            const row = grid[r];
            if (!row) return;
            for (let c = 0; c < columns; c++) {
                const cell = row[c];
                if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                    keys.push(cellKey(r, c));
                }
            }
        });

        return keys;
    }

    /** True if every selected cell resolves to a product cell with an id — used to gate bulk price-change. */
    allHaveProducts(grid: PlanogramGrid): boolean {
        if (this.isEmpty) return true;
        for (const key of this.keys) {
            const { rowIndex, colIndex } = parseCellKeyLocal(key);
            const cell: PlanogramCellData | undefined = grid?.[rowIndex]?.[colIndex];
            if (!cell || cell.type !== 'product' || !cell.product || !(cell.product as Record<string, unknown>).id) {
                return false;
            }
        }
        return true;
    }
}

function parseCellKeyLocal(key: CellKey): { rowIndex: number; colIndex: number } {
    const [r, c] = key.split('-').map(Number);
    return { rowIndex: r, colIndex: c };
}